import { type AdditionalCostsData } from "@wayflyer/flyui-dataviz";
import * as React from "react";
import { type IntlShape, useIntl } from "react-intl";
import { useLoaderData } from "react-router";
import { styled } from "styled-components";

import { Dimensions, MetricTypes } from "../autogen/metrics";
import { type InsightsLoaderReturn } from "../loader";
import { InsightsSingleTableScreen, type InsightsSingleTableScreenProps } from "../single-table-screen";
import { channelsDataTooltipMap } from "../strings/tooltips";
import { getUrls } from "../urls";
import { getAdditionalCostsData, initialAdditionalCosts, useAdditionalCosts } from "../utils/additional-costs-api";
import { getToolTipForColumn, toColumns, toTableRows } from "../utils/dashboard-output-helpers";
import { dateRangePresets, useDateAndTemp } from "../utils/date";
import { type DashboardOutput, getDashboardOutput } from "../utils/fetch-api";
import { type DataPrefix } from "../utils/fetch-dashboard-data";
import fetchDashboardMetaData, { type DashboardMetadata } from "../utils/fetch-dashboard-metadata";
import { hasAdditionalCosts } from "../utils/metrics";

import { strChannelsHint, strChannelsTitle } from "./channels-strings";
import { constructChannelsData, getMetricsFromMetadata } from "./metrics-channels";

const sortRowsDefault = (rowA, rowB, columnId, desc) => {
  // sort by raw data if found under the following id pattern.
  // otherwise sort by the data being displayed
  const sortIdStr = `sort_${columnId}`;
  const sortId = rowA.original[sortIdStr] ? sortIdStr : columnId;
  if (rowA.original["Channel"] === "Summary") {
    return desc ? 1 : -1;
  }
  if (rowB.original["Channel"] === "Summary") {
    return desc ? -1 : 1;
  }
  if (rowA.original[sortId] > rowB.original[sortId]) {
    return 1;
  }
  if (rowB.original[sortId] > rowA.original[sortId]) {
    return -1;
  }
  return 0;
};

const INITIAL_PAGE_SIZE = 50;

const SummaryContainer = styled.div`
  margin: 0 20px 10px;
`;

const METRICS_TO_FETCH = [
  MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_BY_REFUNDED_DATE_PLUS_SHIPPING_PLUS_TAX,
  MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_LESS_REFUNDS_FROM_ORDERS_PLUS_SHIPPING_PLUS_TAX,
  MetricTypes.GROSS_REVENUE_LESS_DISCOUNTS_PLUS_SHIPPING_PLUS_TAX,
  MetricTypes.NEW_CUSTOMERS,
  MetricTypes.TOTAL_ORDER_QUANTITY,
];

const getMetricsToDisplay = (
  total_sales_metric: MetricTypes,
  useAdditionalCosts: boolean,
  metadata: DashboardMetadata
): MetricTypes[] => {
  const base = [
    total_sales_metric,
    MetricTypes.NEW_CUSTOMERS,
    MetricTypes.TOTAL_ORDER_QUANTITY,
    MetricTypes.REFUNDS_PERCENT,
  ];
  if (useAdditionalCosts && metadata.supports_profit_analysis) {
    base.push(MetricTypes.COGS, MetricTypes.GROSS_PROFIT, MetricTypes.GROSS_MARGIN_PERCENTAGE);
  }
  return base;
};

const getCombinedData = (
  metadata: DashboardMetadata,
  additionalCosts: AdditionalCostsData,
  overviewDashboardOutput: DashboardOutput,
  currentDashboardOutput: DashboardOutput,
  historicDashboardOutput: DashboardOutput,
  intl: IntlShape
): InsightsSingleTableScreenProps["tableProps"] => {
  // Augment with additional costs and historic magic refunds
  const newDashOutput = constructChannelsData(
    metadata,
    additionalCosts,
    overviewDashboardOutput,
    currentDashboardOutput,
    historicDashboardOutput
  );

  const { total_sales_metric } = getMetricsFromMetadata(metadata);
  const useAdditionalCosts = metadata.supports_profit_analysis && hasAdditionalCosts(additionalCosts);

  const { data: tableRows, summary } = toTableRows(newDashOutput, Dimensions.CHANNEL);

  return {
    columns: toColumns(
      newDashOutput,
      Dimensions.CHANNEL,
      [],
      "left",
      getMetricsToDisplay(total_sales_metric, useAdditionalCosts, metadata),
      intl
    ),
    data: tableRows,
    initialPageSize: INITIAL_PAGE_SIZE,
    enablePagination: tableRows.length > INITIAL_PAGE_SIZE,
    sortBy: [{ id: total_sales_metric, desc: true }],
    getToolTipForColumn: getToolTipForColumn(intl),
    getRowStyle: (record) => {
      return {
        fontWeight: record["_is_summary"] === 1 ? "bold" : "normal",
      };
    },
    getToolTipForData: (record, columnId) => {
      return channelsDataTooltipMap[record[columnId]];
    },
    sortRows: sortRowsDefault,
    summaryRow: summary,
  };
};

/**
 * Entry point
 */
const ChannelsScreen = () => {
  const { customerDomain }: InsightsLoaderReturn = useLoaderData();
  const { dashboardDataPath, additionalCostsPath } = getUrls(customerDomain);
  const [errorChannels, setErrorChannels] = React.useState(null);

  const [metadata, setMetadata] = React.useState<DashboardMetadata>(null);

  const [additionalCosts, setAdditionalCosts] = useAdditionalCosts(additionalCostsPath);

  const [combinedData, setCombinedData] = React.useState<InsightsSingleTableScreenProps["tableProps"]>({
    columns: [],
    data: [],
  });

  const [isLoadingData, setIsLoadingData] = React.useState(true);
  const [hasCombinedData, setHasCombinedData] = React.useState(false);

  const [currentDashboardOutput, setCurrentDashboardOutput] = React.useState(null);
  const [historicDashboardOutput, setHistoricDashboardOutput] = React.useState(null);
  const [overviewDashboardOutput, setOverviewDashboardOutput] = React.useState(null);

  const intl = useIntl();

  const { handleTimeRangeChange, selectedTimeRange, currentPreset } = useDateAndTemp(() => {
    setCurrentDashboardOutput(null);
    setHistoricDashboardOutput(null);
    setOverviewDashboardOutput(null);
    setHasCombinedData(false);
  });

  const fetchData = React.useCallback(() => {
    setMetadata(null);
    setErrorChannels(null);

    fetchDashboardMetaData(
      dashboardDataPath,
      (data) => {
        setMetadata(data);
      },
      (error) => {
        setErrorChannels(error);
      }
    );
  }, [dashboardDataPath]);

  React.useEffect(() => {
    getAdditionalCostsData(
      additionalCostsPath,
      (additionalCostsData: AdditionalCostsData) => {
        setAdditionalCosts(additionalCostsData);
      },
      () => {
        setAdditionalCosts(initialAdditionalCosts);
      }
    );
  }, [additionalCostsPath, setAdditionalCosts]);

  React.useEffect(() => {
    const onError = (e) => setErrorChannels(e);

    getDashboardOutput(
      dashboardDataPath,
      [
        {
          metrics: METRICS_TO_FETCH,
          filename: "channels",
        },
      ],
      selectedTimeRange,
      (data) => {
        setOverviewDashboardOutput(data);
      },
      null,
      onError
    );

    const dataToFetch = [
      { metrics: METRICS_TO_FETCH, filename: "channels" as DataPrefix, groupBys: [Dimensions.CHANNEL] },
    ];

    getDashboardOutput(
      dashboardDataPath,
      dataToFetch,
      selectedTimeRange,
      (data) => {
        setCurrentDashboardOutput(data);
      },
      Dimensions.CHANNEL,
      onError
    );

    getDashboardOutput(
      dashboardDataPath,
      dataToFetch,
      { start: selectedTimeRange.start.clone().subtract(90, "d"), end: selectedTimeRange.end },
      (data) => {
        setHistoricDashboardOutput(data);
      },
      Dimensions.CHANNEL,
      onError
    );
  }, [dashboardDataPath, selectedTimeRange]);

  React.useEffect(() => {
    if (
      !hasCombinedData &&
      currentDashboardOutput &&
      historicDashboardOutput &&
      overviewDashboardOutput &&
      metadata != null
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      setCombinedData(
        getCombinedData(
          metadata,
          additionalCosts,
          overviewDashboardOutput,
          currentDashboardOutput,
          historicDashboardOutput,
          intl
        )
      );
      setIsLoadingData(false);
      setHasCombinedData(true);
    }
  }, [
    additionalCosts,
    currentDashboardOutput,
    hasCombinedData,
    historicDashboardOutput,
    intl,
    metadata,
    overviewDashboardOutput,
  ]);

  React.useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    fetchData();
  }, [fetchData]);

  return (
    <InsightsSingleTableScreen
      dataError={errorChannels}
      dataLoading={isLoadingData}
      tableProps={combinedData}
      pageTitle={intl.formatMessage(strChannelsTitle)}
      hint={
        <SummaryContainer>
          <span>{intl.formatMessage(strChannelsHint)}</span>
        </SummaryContainer>
      }
      datePickerHeaderProps={{
        currentPreset,
        startDate: selectedTimeRange.start.format("YYYY-MM-DD"),
        endDate: selectedTimeRange.end.format("YYYY-MM-DD"),
        dateRangePresets,
        onChange: (dateRangePreset, startDate, endDate) =>
          handleTimeRangeChange(dateRangePreset as Parameters<typeof handleTimeRangeChange>[0], startDate, endDate),
      }}
      // TODO remove when interface allows
      dateRangeOptions={{}}
      onSelectedDateRange={() => {}}
      selectedDateRange={null}
    />
  );
};

export default ChannelsScreen;
